import React, { useState, useEffect, useRef } from 'react';
import { ChordVoicing } from '../utils/guitarChords';
import GuitarFretboard from './GuitarFretboard';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ChordNavigatorProps {
  voicings: ChordVoicing[];
}

const ChordNavigator: React.FC<ChordNavigatorProps> = ({ voicings }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const minSwipeDistance = 50;

  // Track voicings identity to detect real changes (not just reference changes)
  const voicingsKey = voicings.length > 0 ? `${voicings[0].name}-${voicings.length}` : '';

  // Reset index when voicings change (new chord selected)
  useEffect(() => {
    setCurrentIndex(0);
  }, [voicingsKey]);

  if (voicings.length === 0) return null;

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);
    
    if (touchStart !== null) {
      const diff = currentTouch - touchStart;
      const limitedOffset = Math.max(-100, Math.min(100, diff * 0.5));
      setSwipeOffset(limitedOffset);
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setSwipeOffset(0);
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      setCurrentIndex((prev) => (prev < voicings.length - 1 ? prev + 1 : 0));
    } else if (isRightSwipe) {
      setCurrentIndex((prev) => (prev > 0 ? prev - 1 : voicings.length - 1));
    }
    
    setSwipeOffset(0);
    setTouchStart(null);
    setTouchEnd(null);
  };

  const currentVoicing = voicings[currentIndex];

  const handlePrev = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : voicings.length - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < voicings.length - 1 ? prev + 1 : 0));
  };

  return (
    <div className="flex flex-col items-center w-full max-w-sm mx-auto">
      {/* Navigator Container - overflow-visible to allow arrows to extend */}
      <div className="flex items-center justify-center overflow-visible">
        {/* Relative wrapper centered on the card - overflow-visible for arrows */}
        <div className="relative group overflow-visible">
          {/* Previous Button - "Merged" into the left border - Hidden on mobile */}
          <button
            onClick={handlePrev}
            type="button"
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-slate-900 border border-slate-700 text-slate-500 hover:text-emerald-400 hover:border-emerald-500/50 hover:bg-slate-800 transition-all active:scale-90 z-30 hidden sm:flex shadow-2xl backdrop-blur-sm group-hover:border-emerald-500/30"
            aria-label="Previous voicing"
          >
            <ChevronLeft size={20} strokeWidth={3} />
          </button>

          {/* Swipeable wrapper */}
          <div 
            ref={containerRef}
            className="relative"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {/* The Diagram with gradient fade during swipe */}
            <div 
              className="relative"
              style={{ 
                transform: `translateX(${swipeOffset}px)`,
                transition: swipeOffset === 0 ? 'transform 0.2s ease-out' : 'none'
              }}
            >
              <div
                style={{
                  maskImage: swipeOffset !== 0
                    ? swipeOffset > 0 
                      ? `linear-gradient(to right, rgba(0,0,0,1) 40%, rgba(0,0,0,${Math.max(0.05, 1 - Math.abs(swipeOffset) / 60)}) 70%)`
                      : `linear-gradient(to left, rgba(0,0,0,1) 40%, rgba(0,0,0,${Math.max(0.05, 1 - Math.abs(swipeOffset) / 60)}) 70%)`
                    : 'none',
                  WebkitMaskImage: swipeOffset !== 0
                    ? swipeOffset > 0 
                      ? `linear-gradient(to right, rgba(0,0,0,1) 40%, rgba(0,0,0,${Math.max(0.05, 1 - Math.abs(swipeOffset) / 60)}) 70%)`
                      : `linear-gradient(to left, rgba(0,0,0,1) 40%, rgba(0,0,0,${Math.max(0.05, 1 - Math.abs(swipeOffset) / 60)}) 70%)`
                    : 'none'
                }}
              >
                <GuitarFretboard voicing={currentVoicing} />
              </div>
            </div>
          </div>

          {/* Next Button - "Merged" into the right border - Hidden on mobile */}
          <button
            onClick={handleNext}
            type="button"
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-slate-900 border border-slate-700 text-slate-500 hover:text-emerald-400 hover:border-emerald-500/50 hover:bg-slate-800 transition-all active:scale-90 z-30 hidden sm:flex shadow-2xl backdrop-blur-sm group-hover:border-emerald-500/30"
            aria-label="Next voicing"
          >
            <ChevronRight size={20} strokeWidth={3} />
          </button>
        </div>
      </div>

      {/* Pagination Indicators */}
      <div className="mt-8 flex flex-col items-center gap-3">
        <div className="flex gap-1.5">
          {voicings.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`h-1 rounded-full transition-all duration-300 ${
                idx === currentIndex ? 'w-6 bg-emerald-500' : 'w-1.5 bg-slate-800 hover:bg-slate-700'
              }`}
            />
          ))}
        </div>
        <span className="text-[9px] uppercase tracking-[0.2em] font-black text-slate-600">
          Position {currentIndex + 1} of {voicings.length}
        </span>
      </div>
    </div>
  );
};

export default ChordNavigator;